import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { TASK_STATUSES, PRIORITIES, item } from "@/lib/constants";
import { fmtDate, isOverdue, initials } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Card } from "@/components/ui/card";
import { Plus, CalendarClock, CheckSquare } from "lucide-react";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

function TaskCard({ t, onClick, onDragStart }) {
  const pr = item(PRIORITIES, t.priority);
  const overdue = isOverdue(t.due_date, t.status);
  const checkTotal = t.checklist?.length || 0;
  const checkDone = t.checklist?.filter((c) => c.done).length || 0;
  return (
    <div draggable onDragStart={(e) => onDragStart(e, t)} onClick={() => onClick(t.id)}
      className="bg-card border rounded-lg p-2 cursor-pointer hover:shadow-md hover:border-indigo-300 transition-all active:cursor-grabbing"
      data-testid={`kanban-card-${t.id}`}>
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-mono text-muted-foreground">#{t.number}</span>
        <Badge variant="outline" className={`text-[9px] px-1 py-0 h-4 ${pr.chip}`}>{pr.label}</Badge>
        {checkTotal > 0 && <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><CheckSquare className="h-3 w-3" />{checkDone}/{checkTotal}</span>}
        <span className="h-1.5 w-1.5 rounded-full ml-auto shrink-0" style={{ background: pr.color }} />
      </div>
      <p className="text-[13px] font-medium mt-1 line-clamp-2 leading-snug">{t.title}</p>
      <div className="flex items-center justify-between mt-1.5">
        {t.due_date ? (
          <span className={`text-[10px] flex items-center gap-1 ${overdue ? "text-rose-600 font-medium" : "text-muted-foreground"}`}>
            <CalendarClock className="h-3 w-3" />{fmtDate(t.due_date)}
          </span>
        ) : <span />}
        <div className="flex -space-x-1.5">
          {(t.assignees || []).slice(0, 3).map((a, i) => (
            <Avatar key={i} className="h-5 w-5 border-2 border-card"><AvatarFallback className="text-[8px]">{initials(a)}</AvatarFallback></Avatar>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function KanbanBoard({ tasks, onOpen, onAdd, membersMap = {} }) {
  const qc = useQueryClient();
  const [dragOver, setDragOver] = useState(null);

  const onDragStart = (e, t) => { e.dataTransfer.setData("taskId", t.id); e.dataTransfer.effectAllowed = "move"; };
  const onDrop = async (e, status) => {
    e.preventDefault();
    setDragOver(null);
    const taskId = e.dataTransfer.getData("taskId");
    if (!taskId) return;
    const count = tasks.filter((t) => t.status === status).length;
    // optimistic
    qc.setQueriesData({ queryKey: ["tasks"] }, (old) => Array.isArray(old) ? old.map((t) => t.id === taskId ? { ...t, status } : t) : old);
    try { await api.post("/tasks/reorder", { task_id: taskId, status, order: count }); }
    finally { qc.invalidateQueries({ queryKey: ["tasks"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); }
  };

  const mapAssignees = (t) => ({ ...t, assignees: (t.assignees || []).map((id) => membersMap[id] || "") });

  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      {TASK_STATUSES.map((s) => {
        const col = tasks.filter((t) => t.status === s.id).map(mapAssignees);
        return (
          <div key={s.id} className={`kanban-col w-64 shrink-0 rounded-xl bg-muted/40 flex flex-col max-h-[calc(100vh-200px)] ${dragOver === s.id ? "drag-over" : ""}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(s.id); }}
            onDragLeave={() => setDragOver(null)} onDrop={(e) => onDrop(e, s.id)}
            data-testid={`kanban-col-${s.id}`}>
            <div className="flex items-center gap-2 px-3 py-2 sticky top-0">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} />
              <span className="text-sm font-semibold">{s.label}</span>
              <Badge variant="secondary" className="text-[10px] ml-auto">{col.length}</Badge>
            </div>
            <div className="flex-1 overflow-y-auto px-1.5 space-y-1.5">
              {col.map((t) => <TaskCard key={t.id} t={t} onClick={onOpen} onDragStart={onDragStart} />)}
            </div>
            <button onClick={() => onAdd(s.id)} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground px-3 py-2 transition-colors" data-testid={`kanban-add-${s.id}`}>
              <Plus className="h-3.5 w-3.5" /> Görev ekle
            </button>
          </div>
        );
      })}
    </div>
  );
}
